import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { appDraftKeys } from '~/lib/query-keys';

/**
 * FRONT-2940: an app's drafts (`GET /api/admin/apps/:slug/drafts`), for its
 * owner and editors while `eddy.app-drafts` is on; an editor asking the owner
 * to accept their draft; the owner deleting a removed editor's draft.
 */

export interface DraftFileChange {
  path: string;
  status: 'added' | 'changed' | 'removed';
  added: number;
  removed: number;
}

export interface DraftChanges {
  files: DraftFileChange[];
  added: number;
  removed: number;
}

export interface AppDraft {
  userId: string;
  name: string | null;
  isYou: boolean;
  isOwnersDraft: boolean;
  workingNow: boolean;
  /** Null when the draft's changes could not be read right now. */
  summary: string | null;
  behind: boolean | null;
  changes: DraftChanges | null;
  /** FRONT-3159: the draft's version as the list read it; null when unknown. */
  tip?: string | null;
  /** Its person no longer edits the app; shown to the owner only. */
  inactive: boolean;
  /** FRONT-3161: on the viewing editor's own draft only; `none` when the app already has everything in it, null when unknown. */
  reviewState: 'not-sent' | 'sent' | 'none' | null;
  /** FRONT-3159: the app already holds everything in it; every row for the owner, an editor's own row, else null. Null when unknown. */
  inApp: boolean | null;
}

export interface AskAnswer {
  owner: string;
  alreadyAsked: boolean;
}

export function useAppDrafts(slug: string, enabled = true) {
  return useQuery({
    queryKey: appDraftKeys.list(slug),
    queryFn: () => api.get<{ drafts: AppDraft[] }>(`/api/admin/apps/${encodeURIComponent(slug)}/drafts`),
    enabled: enabled && !!slug,
  });
}

export function useAskOwnerToAccept(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    // FRONT-3159: an optional note for the owner, in the editor's own words.
    mutationFn: (note: string = '') => api.post<AskAnswer>(`/api/admin/apps/${encodeURIComponent(slug)}/drafts/ask`, { note }),
    // The Edit page's notice reads "sent" from the list.
    onSuccess: () => qc.invalidateQueries({ queryKey: appDraftKeys.list(slug) }),
  });
}

export function useDeleteDraft(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.delete<{ deleted: boolean }>(`/api/admin/apps/${encodeURIComponent(slug)}/drafts/${encodeURIComponent(userId)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: appDraftKeys.list(slug) }),
  });
}
